import {
  View,
  Text,
  TextInput,
  Pressable,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  type NativeSyntheticEvent,
  type TextInputKeyPressEventData,
  StyleSheet,
} from 'react-native';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { trpc } from '../../lib/trpc';
import {
  AiDataSharingConsent,
  LoadingSpinner,
  PageContainer,
  PageHeader,
  WebHead,
} from '../../components';
import { MarkdownMessage } from '../../components/MarkdownMessage';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useColors } from '../../lib/colors';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Tool calls in progress during this assistant turn */
  toolCalls?: string[];
}

// ─── Suggestion chips ─────────────────────────────────────────────────────────

const SUGGESTIONS = [
  'What is my net worth?',
  'How much did I spend last month?',
  'When can I retire?',
  'What if I cut expenses by $500/month?',
  'What is my savings rate?',
  'How is my budget tracking this quarter?',
];

// ─── Tool name formatting ─────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  getFinancialSummary: 'Looking up your financial summary…',
  getTransactions: 'Fetching transactions…',
  getBudgetVsActual: 'Comparing budget vs. actual…',
  getFirePlanAnalysis: 'Analyzing your FIRE plan results…',
  runFireScenario: 'Running FIRE scenario…',
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  chatBody: {
    flex: 1,
  },
  messagesList: {
    flex: 1,
  },
});

// ─── Sub-components ───────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  const c = useColors();
  return (
    <View className={`max-w-[85%] ${isUser ? 'self-end' : 'self-start'}`}>
      {/* Tool call indicators for assistant messages */}
      {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
        <View className="mb-1.5 gap-1">
          {message.toolCalls.map((toolName) => (
            <View
              key={toolName}
              className="flex-row items-center gap-1.5 self-start bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full"
            >
              <ActivityIndicator size="small" color={c.primary} />
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                {TOOL_LABELS[toolName] ?? `Using ${toolName}…`}
              </Text>
            </View>
          ))}
        </View>
      )}
      <View
        className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-forest-600 rounded-tr-sm'
            : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-tl-sm'
        }`}
      >
        <MarkdownMessage content={message.content} isUser={isUser} />
      </View>
    </View>
  );
}

function TypingIndicator({ toolCalls }: { toolCalls: string[] }) {
  const c = useColors();
  if (toolCalls.length > 0) {
    return (
      <View className="self-start max-w-[85%] gap-1 mb-2">
        {toolCalls.map((toolName) => (
          <View
            key={toolName}
            className="flex-row items-center gap-1.5 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full"
          >
            <ActivityIndicator size="small" color={c.primary} />
            <Text className="text-xs text-gray-500 dark:text-gray-400">
              {TOOL_LABELS[toolName] ?? `Using ${toolName}…`}
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return (
    <View className="self-start bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm mb-2">
      <View className="flex-row gap-1 items-center">
        <View className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
        <View className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
        <View className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
      </View>
    </View>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export default function AIChatScreen() {
  const isDesktop = useIsDesktop();
  const utils = trpc.useUtils();
  const params = useLocalSearchParams<{
    prompt?: string | string[];
    autoSend?: string | string[];
    autoRunKey?: string | string[];
  }>();
  const listRef = useRef<FlatList>(null);
  const inputRef = useRef<TextInput>(null);
  const consumedAutoRunKeyRef = useRef<string | null>(null);

  // PD-023: The screen lives inside a tab navigator whose tab bar sits below
  // the content. KeyboardAvoidingView needs to know about it so the input bar
  // stays visible above the keyboard.  The default iOS tab bar height is 49,
  // plus ~34 for the home indicator on modern iPhones.
  const TAB_BAR_HEIGHT = Platform.OS === 'ios' ? 83 : 0;

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        "Hi! I'm your AI financial planning assistant. I can look up your real financial data and answer questions about your spending, savings rate, and FIRE timeline. What would you like to know?",
    },
  ]);

  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const streamingTextRef = useRef(''); // ref to avoid stale closure in onData
  const [activeToolCalls, setActiveToolCalls] = useState<string[]>([]);
  const householdQ = trpc.households.get.useQuery();
  const setAiDataSharingConsentMutation = trpc.households.setAiDataSharingConsent.useMutation({
    onSuccess: () => {
      void utils.households.get.invalidate();
    },
  });

  // The messages sent to the API — user + assistant turns only
  const [apiMessages, setApiMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const hasAiDataSharingConsent = householdQ.data?.hasAiDataSharingConsent ?? false;
  const selectedAiProvider = householdQ.data?.aiProvider ?? 'google';
  const aiConsentError = setAiDataSharingConsentMutation.error?.message ?? null;

  // ─── Subscription ─────────────────────────────────────────────────────────

  trpc.ai.chat.useSubscription(
    { messages: apiMessages },
    {
      enabled: hasAiDataSharingConsent && isStreaming && apiMessages.length > 0,
      onData(event) {
        switch (event.type) {
          case 'delta':
            setStreamingText((prev) => {
              const next = prev + event.text;
              streamingTextRef.current = next;
              return next;
            });
            // Clear tool calls when text starts flowing
            setActiveToolCalls([]);
            setTimeout(() => listRef.current?.scrollToEnd({ animated: false }), 50);
            break;

          case 'tool_call':
            setActiveToolCalls((prev) =>
              prev.includes(event.toolName) ? prev : [...prev, event.toolName],
            );
            break;

          case 'tool_result':
            // Tool finished — Claude is processing the result before continuing
            setActiveToolCalls((prev) => prev.filter((t) => t !== event.toolName));
            break;

          case 'done': {
            const finalText = streamingTextRef.current;
            // Commit the streamed reply to messages
            const assistantMsg: Message = {
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: finalText,
            };
            setMessages((prev) => [...prev, assistantMsg]);
            // Update API messages history for next turn
            setApiMessages((prev) => [
              ...prev,
              { role: 'assistant', content: finalText },
            ]);
            streamingTextRef.current = '';
            setStreamingText('');
            setActiveToolCalls([]);
            setIsStreaming(false);
            break;
          }

          case 'error':
            setMessages((prev) => [
              ...prev,
              {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Sorry, something went wrong: ${event.message}. Please try again.`,
              },
            ]);
            streamingTextRef.current = '';
            setStreamingText('');
            setActiveToolCalls([]);
            setIsStreaming(false);
            break;
        }
      },
      onError(err) {
        const message = err.message?.trim() || 'The AI stream ended unexpectedly';
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `Connection error: ${message}. Please try again.`,
          },
        ]);
        setStreamingText('');
        setActiveToolCalls([]);
        setIsStreaming(false);
      },
    },
  );

  // ─── Send handler ─────────────────────────────────────────────────────────

  const sendMessage = useCallback((text: string) => {
    const normalized = text.trim();
    if (!normalized || isStreaming || !hasAiDataSharingConsent) return;

    // Add user message to UI
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: normalized,
    };
    setMessages((prev) => [...prev, userMsg]);

    // Build new API messages array (history + new user message)
    const newApiMessages: { role: 'user' | 'assistant'; content: string }[] = [
      ...apiMessages,
      { role: 'user', content: normalized },
    ];
    setApiMessages(newApiMessages);
    setInput('');
    streamingTextRef.current = '';
    setStreamingText('');
    setActiveToolCalls([]);
    setIsStreaming(true);

    setTimeout(() => listRef.current?.scrollToEnd({ animated: true }), 100);
  }, [apiMessages, hasAiDataSharingConsent, isStreaming]);

  const handleSend = useCallback(() => {
    sendMessage(input);
  }, [input, sendMessage]);

  // PD-022: On web, Enter submits and Shift+Enter inserts a newline.
  // On mobile, onSubmitEditing handles the Return key (see TextInput props).
  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Platform.OS !== 'web') return;
      const nativeEvent = e.nativeEvent as TextInputKeyPressEventData & {
        shiftKey?: boolean;
      };
      if (nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  useEffect(() => {
    const first = (value: string | string[] | undefined) =>
      Array.isArray(value) ? value[0] : value;
    const prompt = first(params.prompt)?.trim();
    const autoSend = first(params.autoSend);
    const autoRunKey = first(params.autoRunKey) ?? prompt ?? null;

    if (!prompt || autoSend !== '1' || autoRunKey === null || isStreaming) {
      return;
    }
    if (consumedAutoRunKeyRef.current === autoRunKey) return;

    consumedAutoRunKeyRef.current = autoRunKey;
    sendMessage(prompt);
  }, [params.prompt, params.autoSend, params.autoRunKey, isStreaming, sendMessage]);

  // ─── Render ───────────────────────────────────────────────────────────────

  // Combine persisted messages with streaming text for display
  const displayMessages = messages;
  const showTyping =
    isStreaming && (streamingText === '' || activeToolCalls.length > 0);
  const showStreamingBubble = isStreaming && streamingText !== '';

  if (householdQ.isLoading) {
    return <LoadingSpinner message="Loading Ask AI..." />;
  }

  if (!hasAiDataSharingConsent) {
    return (
      <>
        <WebHead><title>AI Advisor – Per Diem</title></WebHead>
        <View className="flex-1 bg-canvas dark:bg-canvas-dark">
          <PageContainer className="py-8" size="wide">
            <PageHeader
              title="AI Advisor"
              subtitle="Grant permission before Per Diem sends data to your selected AI provider."
            />
            <AiDataSharingConsent
              provider={selectedAiProvider}
              loading={setAiDataSharingConsentMutation.isPending}
              error={aiConsentError}
              onAccept={() => {
                setAiDataSharingConsentMutation.mutate({ granted: true });
              }}
            />
          </PageContainer>
        </View>
      </>
    );
  }

  return (
    <>
      <WebHead><title>AI Advisor – Per Diem</title></WebHead>
      <SafeAreaView
        style={styles.screen}
        className="bg-canvas dark:bg-canvas-dark"
        edges={['top', 'left', 'right']}
      >
      <KeyboardAvoidingView
        style={styles.screen}
        className="bg-canvas dark:bg-canvas-dark"
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={
          Platform.OS === 'ios'
            ? TAB_BAR_HEIGHT
            : 0
        }
      >
      <View
        style={styles.chatBody}
        className={isDesktop ? 'max-w-[800px] w-full mx-auto' : ''}
      >
      <View className={`${isDesktop ? 'px-8 pt-8' : 'px-4 pt-4 pb-2'}`}>
        <PageHeader
          title="AI Advisor"
          subtitle="Ask questions about your spending, savings, and FIRE timeline."
        />
      </View>
      <FlatList
        style={styles.messagesList}
        ref={listRef}
        data={displayMessages}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ padding: isDesktop ? 32 : 16, gap: 12 }}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => <MessageBubble message={item} />}
        ListFooterComponent={
          <>
            {/* Streaming text (not yet committed to messages) */}
            {showStreamingBubble && (
              <View className="self-start max-w-[85%] mt-3">
                <View
                  className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm"
                >
                  <MarkdownMessage content={streamingText} isUser={false} />
                </View>
              </View>
            )}
            {/* Typing / tool-call indicator */}
            {showTyping && (
              <View className="mt-3">
                <TypingIndicator toolCalls={activeToolCalls} />
              </View>
            )}
            {/* Suggestion chips (only on first load) */}
            {!isStreaming && messages.length === 1 && (
              <View className="mt-4">
                <Text className="text-xs text-gray-400 dark:text-gray-500 mb-2 ml-1">Try asking:</Text>
                <View className="flex-row flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <Pressable
                      key={s}
                      onPress={() => {
                        setInput(s);
                      }}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full px-3 py-1.5"
                    >
                      <Text className="text-xs text-gray-700 dark:text-gray-300">{s}</Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            )}
          </>
        }
        onContentSizeChange={() =>
          listRef.current?.scrollToEnd({ animated: false })
        }
      />

      {/* Input bar */}
      <View className={`flex-row items-end gap-2 pt-3 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 ${isDesktop ? 'px-8 pb-6' : 'px-4 pb-8'}`}>
        <TextInput
          ref={inputRef}
          value={input}
          onChangeText={setInput}
          placeholder={isStreaming ? 'Responding…' : 'Ask about your finances…'}
          placeholderTextColor="#9ca3af"
          className="flex-1 rounded-2xl bg-gray-100 dark:bg-gray-700 px-4 py-3 text-base text-gray-800 dark:text-gray-100 max-h-28"
          returnKeyType="send"
          blurOnSubmit={false}
          onSubmitEditing={handleSend}
          onKeyPress={handleKeyPress}
          multiline
          editable={!isStreaming}
        />
        <Pressable
          onPress={handleSend}
          disabled={!input.trim() || isStreaming}
          className={`rounded-full w-11 h-11 items-center justify-center ${
            input.trim() && !isStreaming ? 'bg-forest-600' : 'bg-gray-200 dark:bg-gray-600'
          }`}
        >
          {isStreaming ? (
            <ActivityIndicator size="small" color="#9ca3af" />
          ) : (
            <Text
              className={`text-lg ${
                input.trim() ? 'text-white' : 'text-gray-400'
              }`}
            >
              ↑
            </Text>
          )}
        </Pressable>
      </View>
      </View>
      </KeyboardAvoidingView>
      </SafeAreaView>
    </>
  );
}
